import { ChangeEvent, FC, useState } from "react";
import styled from "styled-components";
import css from "@styled-system/css";

import { BasePaginationProps, UsePaginationItem } from "./pagination.type";
import PaginationItem from "./PaginationItem";
import usePagination from "./usePagination";
import PaginationTotals from "./PaginationTotals";

const PaginationUl = styled.ul`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 0;
  margin: 0;
  list-style: none;
  & {
    li:last-of-type > * {
      margin-right: 0;
    }
  }

  ${css({
    justifyContent: ["center", "start"],
  })}
`;

export interface PaginationProps extends BasePaginationProps {
  /**
   *The element's unique identifier
   */
  id?: string;
  /**
   * Render the item.
   * @param {PaginationRenderItemParams} params The props to spread on a PaginationItem.
   * @returns {ReactNode}
   * @default (item) => <PaginationItem {...item} />
   */
  renderItem?: (item: UsePaginationItem) => JSX.Element;
}

function getAriaLabel(type: string, page: number, selected: boolean) {
  if (type === "page") {
    return `${selected ? "" : "Go to "}page ${page}`;
  }
  return `Go to ${type} page`;
}

const Pagination: FC<PaginationProps> = (props) => {
  const {
    id,
    count: pageCount,
    totalItemsCount,
    itemsPerPage,
    page,
    onChange,
  } = props;
  const [currentPage, setCurrentPage] = useState(page);

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    if (onChange) {
      onChange(event, page);
    }
  };

  const { items } = usePagination({ ...props, onChange: handlePageChange });

  const {
    renderItem = (item: UsePaginationItem) => <PaginationItem {...item} />,
  } = props;

  return (
    <nav id={id}>
      <PaginationUl>
        {items.map((item, index) => (
          <li key={index}>
            {renderItem({
              ...item,
              "aria-label": getAriaLabel(
                item.type,
                item.page as number,
                item.selected
              ),
            })}
          </li>
        ))}
      </PaginationUl>
      {totalItemsCount && itemsPerPage && (
        <PaginationTotals
          totalItemsCount={totalItemsCount}
          currentPage={currentPage}
          pageCount={pageCount}
          itemsPerPage={itemsPerPage}
        />
      )}
    </nav>
  );
};

export default Pagination;
